/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 12:14:08 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { NumberingSequence } from "lib/schemas";
import { TextFormInputWithTagSelector } from "common/components/context-tags";
import { useNumberingSequenceAPI } from "common/services/NumberingSequenceAPI";
import { useFormBuilderContext } from "form-builder/providers";
import { OSAPIForm, TextFormInput } from "ui-widgets/forms";
import { InputHelpDialogWrapper, EmphasisText } from "ui-widgets";

interface NumberingSequenceFormProps {
  numberingSequenceId: NumberingSequence["id"];
  onAPIResponse?: (response: NumberingSequence) => void;
  numberingSequenceDefault?: NumberingSequence;
}
export const NumberingSequenceForm = ({
  numberingSequenceId,
  onAPIResponse,
  numberingSequenceDefault = null,
}: NumberingSequenceFormProps) => {
  const { useNumberingSequenceAPIForm } = useNumberingSequenceAPI();
  const useAPIFormReturn = useNumberingSequenceAPIForm({
    numberingSequenceId,
    numberingSequenceDefault,
    updateNumberingSequenceFromAPICallback: onAPIResponse,
  });

  const { contextTagsAPIFilters = {} } = useFormBuilderContext();

  return (
    <OSAPIForm useAPIFormReturn={useAPIFormReturn} loaderType="form-skeleton">
      <TextFormInput<NumberingSequence> schema_field_key="name" label="Name" />
      <InputHelpDialogWrapper
        helpTitle="Next Number"
        helpContent="Set the numeric value that should start or override the numbering sequence. This does not apply for alphanumeric identifiers."
      >
        <TextFormInput<NumberingSequence> schema_field_key="number_next" type="number" label="Next Number" />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Number Increment"
        helpContent="Provide the amount by which the numbering sequence will increase based on the previously generated record. For example, if a record is generated with number 01, set number increment to 1 if you want the next record to be 02. This does not apply for alphanumeric identifiers."
      >
        <TextFormInput<NumberingSequence> schema_field_key="number_increment" type="number" label="Number Increment" />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Next Alphanumeric Number"
        helpContent="Set the alphanumeric text that should start or override the numbering sequence. This does not apply for numeric identifiers."
      >
        <TextFormInput<NumberingSequence> schema_field_key="alphanumeric_next" label="Next Alphanumeric Number" />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Number Padding"
        helpContent="Use this setting to configure the padding of the generated number. The number will be padded with zeros e.g a padding of 3 will result in a number such as 001, 002 etc.."
      >
        <TextFormInput<NumberingSequence> schema_field_key="padding" type="number" label="padding" />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Prefix"
        helpContent={
          <>
            Set the prefix of generated identifiers. You can also apply some tags available on Onestop. Tag Options are:{" "}
            <EmphasisText>{"{MonthTextFull}"}</EmphasisText>, <EmphasisText>{"{MonthTextShort}"}</EmphasisText>,{" "}
            <EmphasisText>{"{MonthNumeric}"}</EmphasisText>, <EmphasisText>{"{YearFull}"}</EmphasisText>,{" "}
            <EmphasisText>{"{YearShort}"}</EmphasisText>, <EmphasisText>{"{DayTextFull}"}</EmphasisText>,{" "}
            <EmphasisText>{"{DayTextShort}"}</EmphasisText>, <EmphasisText>{"{DayNumeric}"}</EmphasisText>
          </>
        }
      >
        <TextFormInputWithTagSelector<NumberingSequence>
          schema_field_key="prefix"
          label="Prefix"
          contextTagsAPIFilters={contextTagsAPIFilters}
        />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Suffix"
        helpContent={
          <>
            Set the suffix of generated identifiers. You can also apply some tags available on Onestop. Tag Options are:{" "}
            <EmphasisText>{"{MonthTextFull}"}</EmphasisText>, <EmphasisText>{"{MonthTextShort}"}</EmphasisText>,{" "}
            <EmphasisText>{"{MonthNumeric}"}</EmphasisText>, <EmphasisText>{"{YearFull}"}</EmphasisText>,{" "}
            <EmphasisText>{"{YearShort}"}</EmphasisText>, <EmphasisText>{"{DayTextFull}"}</EmphasisText>,{" "}
            <EmphasisText>{"{DayTextShort}"}</EmphasisText>, <EmphasisText>{"{DayNumeric}"}</EmphasisText>
          </>
        }
      >
        <TextFormInputWithTagSelector<NumberingSequence>
          schema_field_key="suffix"
          label="Suffix"
          contextTagsAPIFilters={contextTagsAPIFilters}
        />
      </InputHelpDialogWrapper>
    </OSAPIForm>
  );
};
